import { AlertTriangle, Plus, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { fetchJson, sendJson } from "../../api/rest";
import {
	btn,
	Empty,
	ErrorBanner,
	input,
	Panel,
	Spinner,
} from "../../components/flows/ui";
import type { CalendarPreview, Theme } from "../../types/flows";

// A theme's window is MM-DD, so it recurs every year and the strip below is a
// calendar year rather than a date range. The lead-in is drawn as well as the
// window, because writing for April demand happens in March and that is the
// part people forget.

const MONTHS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

const dayOfYear = (mmdd: string): number => {
	const [month, day] = mmdd.split("-").map(Number);
	if (!month || !day) return 0;
	// A non-leap year is the right ruler for a recurring MM-DD window; the point
	// is where in the year it sits, not an exact date.
	const cumulative = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
	return cumulative[month - 1] + day;
};

const pct = (day: number) => (day / 365) * 100;

/** A theme with none of the three mappings filled in emits nothing at all. */
const isUnmapped = (theme: Theme) =>
	theme.categoryIds.length === 0 &&
	theme.keywords.length === 0 &&
	theme.productIds.length === 0;

const YearStrip = ({ themes }: { themes: Theme[] }) => (
	<div className="space-y-2">
		<div className="relative ml-44 h-4">
			{MONTHS.map((month, index) => (
				<span
					key={month}
					className="absolute text-[10px] text-slate-400"
					style={{ left: `${pct(dayOfYear(`${index + 1}-01`))}%` }}
				>
					{month}
				</span>
			))}
		</div>

		{themes.map((theme) => {
			const start = dayOfYear(theme.startsOn);
			const end = dayOfYear(theme.endsOn);
			const wraps = end < start;
			const leadStart = Math.max(0, start - theme.leadDays);
			const unmapped = isUnmapped(theme);

			const segments = wraps
				? [
						{ from: start, to: 365 },
						{ from: 0, to: end },
					]
				: [{ from: start, to: end }];

			return (
				<div key={theme.id} className="flex items-center gap-2">
					<span
						className={`w-42 shrink-0 truncate text-[12px] ${
							theme.enabled ? "text-slate-700" : "text-slate-400 line-through"
						}`}
						title={theme.name}
					>
						{theme.name}
						{unmapped && <span className="ml-1 text-warning-dark">⚠</span>}
					</span>
					<div className="relative h-5 flex-1 rounded bg-slate-50">
						{!wraps && theme.leadDays > 0 && (
							<div
								className="absolute top-0 h-full rounded-l border border-dashed border-slate-300"
								style={{
									left: `${pct(leadStart)}%`,
									width: `${pct(start - leadStart)}%`,
								}}
								title={`${theme.leadDays} days of lead time`}
							/>
						)}
						{segments.map((segment) => (
							<div
								key={`${segment.from}-${segment.to}`}
								className={`absolute top-0 h-full rounded ${
									unmapped
										? "bg-slate-300"
										: theme.active
											? "bg-primary"
											: "bg-primary/45"
								}`}
								style={{
									left: `${pct(segment.from)}%`,
									width: `${Math.max(1, pct(segment.to - segment.from))}%`,
								}}
								title={`${theme.startsOn} to ${theme.endsOn}`}
							/>
						))}
					</div>
					<span className="w-28 shrink-0 text-right text-[11px] text-slate-400">
						{theme.active
							? "running now"
							: theme.daysUntilStart >= 0
								? `in ${theme.daysUntilStart} days`
								: "—"}
					</span>
				</div>
			);
		})}

		<p className="ml-44 text-[10px] text-slate-400">
			Solid bar: the window. Dashed outline: the lead time a flow starts writing
			in. Grey bar: no categories, keywords or products, so it produces nothing.
		</p>
	</div>
);

const emptyTheme = {
	name: "",
	startsOn: "01-01",
	endsOn: "01-31",
	leadDays: 30,
	categoryIds: [] as number[],
	keywords: [] as string[],
	productIds: [] as number[],
	promptHint: "",
	enabled: true,
};

type Draft = typeof emptyTheme & { id?: number };

const numberList = (raw: string): number[] =>
	raw
		.split(/[\s,]+/)
		.map(Number)
		.filter((n) => Number.isInteger(n) && n > 0);

const ThemeEditor = ({
	draft,
	onClose,
	onSaved,
}: {
	draft: Draft;
	onClose: () => void;
	onSaved: () => void;
}) => {
	const [value, setValue] = useState(draft);
	const [categories, setCategories] = useState(draft.categoryIds.join(", "));
	const [products, setProducts] = useState(draft.productIds.join(", "));
	const [keywords, setKeywords] = useState(draft.keywords.join(", "));
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const save = async () => {
		setBusy(true);
		setError(null);
		try {
			const body = {
				name: value.name,
				startsOn: value.startsOn,
				endsOn: value.endsOn,
				leadDays: Number(value.leadDays),
				categoryIds: numberList(categories),
				productIds: numberList(products),
				keywords: keywords
					.split(",")
					.map((word) => word.trim())
					.filter(Boolean),
				promptHint: value.promptHint,
				enabled: value.enabled,
			};
			const result = draft.id
				? await sendJson<{ error?: string }>(
						`/flows/calendar/${draft.id}`,
						"PUT",
						body,
					)
				: await sendJson<{ error?: string }>("/flows/calendar", "POST", body);
			if (result.error) setError(result.error);
			else {
				onSaved();
				onClose();
			}
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	const remove = async () => {
		if (!draft.id) return;
		setBusy(true);
		try {
			await sendJson(`/flows/calendar/${draft.id}`, "DELETE");
			onSaved();
			onClose();
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	const unmapped =
		numberList(categories).length === 0 &&
		numberList(products).length === 0 &&
		keywords.trim() === "";

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4">
			<div className="max-h-[90vh] w-full max-w-lg space-y-4 overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
				<div className="flex items-start justify-between">
					<h3 className="text-[15px] font-semibold text-slate-800">
						{draft.id ? "Edit theme" : "New theme"}
					</h3>
					<button
						type="button"
						onClick={onClose}
						className="rounded p-1 text-slate-400 hover:bg-slate-100"
						aria-label="Close"
					>
						<X className="h-4 w-4" />
					</button>
				</div>

				<label className="block space-y-1">
					<span className="block text-[12px] font-medium text-slate-600">
						Name
					</span>
					<input
						className={input}
						value={value.name}
						onChange={(event) =>
							setValue({ ...value, name: event.target.value })
						}
					/>
				</label>

				<div className="grid grid-cols-3 gap-3">
					<label className="block space-y-1">
						<span className="block text-[12px] font-medium text-slate-600">
							Starts (MM-DD)
						</span>
						<input
							className={input}
							placeholder="03-01"
							value={value.startsOn}
							onChange={(event) =>
								setValue({ ...value, startsOn: event.target.value })
							}
						/>
					</label>
					<label className="block space-y-1">
						<span className="block text-[12px] font-medium text-slate-600">
							Ends (MM-DD)
						</span>
						<input
							className={input}
							placeholder="06-30"
							value={value.endsOn}
							onChange={(event) =>
								setValue({ ...value, endsOn: event.target.value })
							}
						/>
					</label>
					<label className="block space-y-1">
						<span className="block text-[12px] font-medium text-slate-600">
							Lead days
						</span>
						<input
							type="number"
							className={input}
							value={value.leadDays}
							onChange={(event) =>
								setValue({ ...value, leadDays: Number(event.target.value) })
							}
						/>
					</label>
				</div>

				<label className="block space-y-1">
					<span className="block text-[12px] font-medium text-slate-600">
						Category ids
					</span>
					<input
						className={input}
						placeholder="101, 204, 310"
						value={categories}
						onChange={(event) => setCategories(event.target.value)}
					/>
				</label>

				<label className="block space-y-1">
					<span className="block text-[12px] font-medium text-slate-600">
						Keywords
					</span>
					<input
						className={input}
						placeholder="havemøbler, terrasse"
						value={keywords}
						onChange={(event) => setKeywords(event.target.value)}
					/>
				</label>

				<label className="block space-y-1">
					<span className="block text-[12px] font-medium text-slate-600">
						Product ids
					</span>
					<input
						className={input}
						placeholder="4411, 9902"
						value={products}
						onChange={(event) => setProducts(event.target.value)}
					/>
				</label>

				{unmapped && (
					<p className="flex items-start gap-1.5 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-[11px] leading-relaxed text-warning-dark">
						<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
						With no categories, keywords or products this theme emits nothing —
						the Season node would return it and downstream Product lookup would
						find nothing to write about. The seeded themes ship this way on
						purpose, because how a season maps to products is still an open
						decision.
					</p>
				)}

				<label className="block space-y-1">
					<span className="block text-[12px] font-medium text-slate-600">
						Prompt hint
					</span>
					<textarea
						className={`${input} min-h-20`}
						value={value.promptHint}
						onChange={(event) =>
							setValue({ ...value, promptHint: event.target.value })
						}
					/>
				</label>

				<label className="flex items-center gap-2 text-[12px] text-slate-600">
					<input
						type="checkbox"
						checked={value.enabled}
						onChange={(event) =>
							setValue({ ...value, enabled: event.target.checked })
						}
						className="h-4 w-4 rounded border-slate-300 text-primary"
					/>
					Enabled
				</label>

				<ErrorBanner error={error} />

				<div className="flex items-center justify-end gap-2">
					{draft.id && (
						<button
							type="button"
							onClick={remove}
							disabled={busy}
							className={`${btn.danger} mr-auto`}
						>
							<Trash2 className="h-4 w-4" /> Delete
						</button>
					)}
					<button type="button" onClick={onClose} className={btn.ghost}>
						Cancel
					</button>
					<button
						type="button"
						onClick={save}
						disabled={busy}
						className={btn.primary}
					>
						Save
					</button>
				</div>
			</div>
		</div>
	);
};

const FlowsCalendar = () => {
	const [themes, setThemes] = useState<Theme[] | null>(null);
	const [draft, setDraft] = useState<Draft | null>(null);
	const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
	const [preview, setPreview] = useState<CalendarPreview | null>(null);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		try {
			setThemes(await fetchJson<Theme[]>("/flows/calendar"));
		} catch (e) {
			setError(String(e));
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const runPreview = async () => {
		setError(null);
		try {
			setPreview(
				await fetchJson<CalendarPreview>(
					`/flows/calendar/preview?date=${date}`,
				),
			);
		} catch (e) {
			setError(String(e));
		}
	};

	if (!themes) return error ? <ErrorBanner error={error} /> : <Spinner />;

	const unmappedCount = themes.filter(isUnmapped).length;

	return (
		<div className="space-y-4">
			<ErrorBanner error={error} />

			{unmappedCount > 0 && (
				<div className="flex items-start gap-2 rounded-xl border border-warning/30 bg-warning/5 px-4 py-3 text-[12px] leading-relaxed text-warning-dark">
					<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
					<span>
						{unmappedCount} of {themes.length} themes have no categories,
						keywords or products, so they produce nothing when a Season node
						picks them up. That is the state they ship in: how a season maps to
						products is deliberately undecided, and the mapping is filled in per
						theme here.
					</span>
				</div>
			)}

			<Panel
				title="The year"
				hint="windows recur every year — the date is MM-DD, not a fixed date"
				action={
					<button
						type="button"
						onClick={() => setDraft({ ...emptyTheme })}
						className={btn.ghost}
					>
						<Plus className="h-4 w-4" /> New theme
					</button>
				}
			>
				{themes.length === 0 ? (
					<Empty>No themes yet.</Empty>
				) : (
					<YearStrip themes={themes} />
				)}
			</Panel>

			<Panel title="Themes">
				<div className="overflow-x-auto">
					<table className="w-full text-left text-[13px]">
						<thead>
							<tr className="border-b border-slate-200 text-slate-500">
								<th className="pb-2 pr-3 font-medium">Name</th>
								<th className="pb-2 pr-3 font-medium">Window</th>
								<th className="pb-2 pr-3 font-medium">Lead</th>
								<th className="pb-2 pr-3 font-medium">Mapped to</th>
								<th className="pb-2 pr-3 font-medium">State</th>
								<th className="pb-2 font-medium" />
							</tr>
						</thead>
						<tbody>
							{themes.map((theme) => (
								<tr
									key={theme.id}
									className="border-b border-slate-100 last:border-0"
								>
									<td className="py-2.5 pr-3 font-medium text-slate-700">
										{theme.name}
										<p className="font-mono text-[10px] font-normal text-slate-400">
											{theme.slug}
										</p>
									</td>
									<td className="py-2.5 pr-3 text-slate-500">
										{theme.startsOn} → {theme.endsOn}
									</td>
									<td className="py-2.5 pr-3 text-slate-500">
										{theme.leadDays}d
									</td>
									<td className="py-2.5 pr-3 text-slate-500">
										{isUnmapped(theme) ? (
											<span className="text-warning-dark">nothing yet</span>
										) : (
											[
												theme.categoryIds.length > 0 &&
													`${theme.categoryIds.length} categories`,
												theme.keywords.length > 0 &&
													`${theme.keywords.length} keywords`,
												theme.productIds.length > 0 &&
													`${theme.productIds.length} products`,
											]
												.filter(Boolean)
												.join(", ")
										)}
									</td>
									<td className="py-2.5 pr-3">
										{!theme.enabled ? (
											<span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
												disabled
											</span>
										) : theme.active ? (
											<span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">
												running
											</span>
										) : theme.upcoming ? (
											<span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] text-sky-700">
												in lead time
											</span>
										) : (
											<span className="text-[11px] text-slate-400">
												in {theme.daysUntilStart} days
											</span>
										)}
									</td>
									<td className="py-2.5">
										<button
											type="button"
											onClick={() =>
												setDraft({
													id: theme.id,
													name: theme.name,
													startsOn: theme.startsOn,
													endsOn: theme.endsOn,
													leadDays: theme.leadDays,
													categoryIds: theme.categoryIds,
													keywords: theme.keywords,
													productIds: theme.productIds,
													promptHint: theme.promptHint ?? "",
													enabled: theme.enabled,
												})
											}
											className="text-[12px] font-medium text-primary"
										>
											Edit
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Panel>

			<Panel
				title="Preview on a date"
				hint="what a Season node would emit that day"
			>
				<div className="flex flex-wrap items-end gap-3">
					<label className="block space-y-1">
						<span className="block text-[12px] font-medium text-slate-600">
							Date
						</span>
						<input
							type="date"
							className={`${input} w-44`}
							value={date}
							onChange={(event) => setDate(event.target.value)}
						/>
					</label>
					<button type="button" onClick={runPreview} className={btn.primary}>
						Preview
					</button>
				</div>

				{preview && (
					<div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
						<div>
							<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
								Active on {preview.date}
							</p>
							{preview.active.length === 0 ? (
								<p className="text-[12px] text-slate-400">
									Nothing is running.
								</p>
							) : (
								<ul className="space-y-1 text-[12px] text-slate-600">
									{preview.active.map((theme) => (
										<li key={theme.id}>
											{theme.name}
											{isUnmapped(theme) && (
												<span className="ml-1 text-warning-dark">
													— but it maps to nothing, so it emits no products
												</span>
											)}
										</li>
									))}
								</ul>
							)}
						</div>
						<div>
							<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
								Within lead time
							</p>
							{preview.upcoming.length === 0 ? (
								<p className="text-[12px] text-slate-400">
									Nothing is approaching.
								</p>
							) : (
								<ul className="space-y-1 text-[12px] text-slate-600">
									{preview.upcoming.map((theme) => (
										<li key={theme.id}>
											{theme.name} — starts in {theme.daysUntilStart} days
										</li>
									))}
								</ul>
							)}
						</div>
					</div>
				)}
			</Panel>

			{draft && (
				<ThemeEditor
					draft={draft}
					onClose={() => setDraft(null)}
					onSaved={load}
				/>
			)}
		</div>
	);
};

export default FlowsCalendar;
